/**
 * Tank tread marks: a pooled trail of flat quads stamped on the floor as tanks
 * drive. Built for "the map can fill up with treads without lagging":
 *   • ONE draw call (a single InstancedMesh), regardless of mark count.
 *   • Fixed-size circular buffer — constant memory + GPU cost; the oldest mark is
 *     overwritten once the pool is full (the floor keeps a long trailing tail).
 *   • Near-zero per-frame CPU: an instance matrix is written ONLY when a new mark
 *     is dropped (callers gate drops by distance moved), never every frame.
 *
 * Marks carry their own Y so they sit on whatever level (ground or road deck) the
 * tank was on. Cleared between floors.
 */

import * as THREE from "three";

/** Drop a tread mark once a tank has moved this far (squared studs). Shared by
 *  the player (main.ts) and enemies (enemies.ts) so densities stay in lockstep. */
export const TREAD_STEP2 = 7 * 7;

const POOL = 1600;
const HIDDEN = new THREE.Matrix4().makeScale(0, 0, 0);
const _mat = new THREE.Matrix4();
const _pos = new THREE.Vector3();
const _quat = new THREE.Quaternion();
const _axis = new THREE.Vector3(0, 1, 0);
const _one = new THREE.Vector3(1, 1, 1);

/** A flat tread-mark texture: two dark parallel stripes (the two treads). */
function treadTexture(): THREE.CanvasTexture {
  const cv = document.createElement("canvas");
  cv.width = 64; cv.height = 64;
  const ctx = cv.getContext("2d")!;
  ctx.clearRect(0, 0, 64, 64);
  ctx.fillStyle = "rgba(0,0,0,0.55)";
  ctx.fillRect(8, 4, 13, 56);   // left tread
  ctx.fillRect(43, 4, 13, 56);  // right tread
  ctx.fillStyle = "rgba(40,40,40,0.4)"; // light wear speckle
  for (let i = 0; i < 30; i++) ctx.fillRect(Math.random() * 64, Math.random() * 64, 1, 2);
  const t = new THREE.CanvasTexture(cv);
  return t;
}

export class Treads {
  private mesh: THREE.InstancedMesh;
  private next = 0;
  private dirty = false;

  constructor(scene: THREE.Scene) {
    const geom = new THREE.PlaneGeometry(22, 16); // ~tank footprint
    geom.rotateX(-Math.PI / 2); // lay flat on the XZ plane
    const mat = new THREE.MeshBasicMaterial({
      map: treadTexture(),
      transparent: true,
      opacity: 0.5,
      depthWrite: false,
      polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1,
    });
    this.mesh = new THREE.InstancedMesh(geom, mat, POOL);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.castShadow = false;
    this.mesh.receiveShadow = false;
    this.mesh.frustumCulled = false;
    // transparent ground decal: drawn after the opaque floor; depthTest keeps it occluded
    // behind walls/tanks. renderOrder puts it at the BOTTOM of the ground-decal ladder
    // (treads −8 < death marks −7 < water −6 < roads), so these never sort-flicker.
    this.mesh.renderOrder = -8;
    for (let i = 0; i < POOL; i++) this.mesh.setMatrixAt(i, HIDDEN);
    this.mesh.instanceMatrix.needsUpdate = true;
    scene.add(this.mesh);
  }

  /** Stamp a tread mark at (x,z) on level surface `floorY`, oriented to `yaw`. */
  drop(x: number, z: number, floorY: number, yaw: number): void {
    const i = this.next;
    this.next = (this.next + 1) % POOL;
    _pos.set(x, floorY + 0.6, z);
    _quat.setFromAxisAngle(_axis, yaw);
    this.mesh.setMatrixAt(i, _mat.compose(_pos, _quat, _one));
    this.dirty = true;
  }

  /** Flush GPU upload (once per frame). */
  update(): void {
    if (this.dirty) { this.mesh.instanceMatrix.needsUpdate = true; this.dirty = false; }
  }

  /** Wipe all marks (between floors). */
  clear(): void {
    for (let i = 0; i < POOL; i++) this.mesh.setMatrixAt(i, HIDDEN);
    this.mesh.instanceMatrix.needsUpdate = true;
    this.next = 0;
    this.dirty = false;
  }
}
